// Phase 5 stack battle system: turn-based, initiative-ordered, and decided
// largely by army composition. Every stack — friendly or enemy — takes one
// action per round in initiative order; the round ends when they all have.
//
// The interesting decision is made before the battle starts, not during it:
// counters (High Elf Archers vs. dragons, Flame Guard vs. trolls, Ritual Mages
// vs. anchors, Spirit Wardens vs. undead, Pathfinders vs. Dark Elves, Dwarves
// vs. giants and fortifications) multiply damage several-fold, and resistances
// halve it. A badly composed army can still fight and sometimes still win, but
// it bleeds for it and tends to miss the battle's objectives.
//
// Deliberately absent, per the phase's "avoid initially" list: food, morale,
// formations, marching speed, terrain, per-soldier equipment, unit experience
// and tactical movement.

import type {
  AbilityLevel,
  ArmyStack,
  Battle,
  BattleObjective,
  DamageType,
  PermanentState,
  Unit,
  UnitTag,
} from "../types"
import { getUnitById } from "./content"
import { getAbilityLevel, isAtLeastLevel } from "./abilityEngine"
import { getArmorReduction } from "./armorEngine"

export type BattleSide = "friendly" | "enemy"

export type BattleOutcome = "ongoing" | "victory" | "defeat" | "retreat"

/** How a stack is spending its action. Specials come from the unit's own specialAction. */
export type BattleActionKind =
  | "attack"
  | "defend"
  | "volley"
  | "shieldwall"
  | "charge"
  | "regenerate"
  | "banish"
  | "reveal"
  | "sunder"
  | "frenzy"

export interface BattleStackState extends ArmyStack {
  stackKey: string
  currentHealth: number
  maxHealth: number
  /**
   * "player" stacks came from the player's currentArmy and their survivors
   * flow back into it; "ally" stacks are battle-only defenders (a garrison, a
   * mustered contingent) that never join currentArmy.
   */
  owner: "player" | "ally" | "enemy"
  side: BattleSide
  /** Defending this round: incoming damage reduced. Cleared at the start of the next round. */
  defending: boolean
  /** Charged or frenzied: guard is down, incoming damage increased, until the next round. */
  guardDropped: boolean
  /** Hidden: cannot be targeted normally, and strikes harder, until revealed. */
  concealed: boolean
  /** Each stack gets its unit type's special action once per battle. */
  specialUsed: boolean
  /** Rounds this stack must sit out (a volley leaves the crews reloading). */
  skipRounds: number
}

/**
 * Permanent ability bonuses the player brings into every battle. Abilities are
 * meant to unlock content rather than be pure stat sticks, so these stay
 * modest — enough to reward a trained commander, not enough to substitute for
 * bringing the right units.
 */
export interface PlayerBattleModifiers {
  /** Leadership: the whole friendly line hits harder. */
  damageMultiplier: number
  /** Archery: ranged stacks specifically hit harder. */
  rangedDamageMultiplier: number
  /** Survival (and worn armour): the friendly line takes less damage. */
  incomingDamageMultiplier: number
  /** Healing: share of the player's own casualties recovered after a won battle. */
  casualtyRecoveryRate: number
  /** Swordsmanship: how hard the player personally fights, i.e. friendly "hero" stacks. */
  heroDamageMultiplier: number
  /** Horsemastery: friendly "cavalry" stacks hit harder. */
  cavalryDamageMultiplier: number
  /** Arcana: friendly arcane-damage stacks (ritual mages, storm callers) hit harder — including their banishment. */
  arcaneDamageMultiplier: number
  /** Spirit Lore: friendly holy-damage stacks (seraph, spirit wardens) hit harder — including their banishment. */
  holyDamageMultiplier: number
  /** Siegecraft: friendly stacks hit "fortification"/"giant" targets harder — the things that fight like walls. */
  siegeDamageMultiplier: number
  /** Tracking: enemy ambushers that start concealed are already revealed when battle is joined. */
  revealsConcealed: boolean
}

export interface BattleRuntimeState {
  battleId: string
  round: number
  friendlyStacks: BattleStackState[]
  enemyStacks: BattleStackState[]
  rngSeed: number
  /** Stack keys that have already acted this round. */
  actedKeys: string[]
  log: string[]
  outcome: BattleOutcome
  modifiers: PlayerBattleModifiers
  /** Rounds whose reinforcements have already arrived, so they arrive exactly once. */
  reinforcedRounds: number[]
  /** Objectives passed, evaluated when the battle ends. */
  completedObjectiveIds: string[]
  /**
   * Keys of the stacks the friendly side started with. Objectives are scored
   * against these rather than against the live list, so mid-battle
   * reinforcements cannot paper over a stack that was actually lost.
   */
  initialFriendlyKeys: string[]
}

/** Auto-resolve and long grinding fights are cut off here rather than looping forever. */
const MAX_ROUNDS = 60

const DEFEND_DAMAGE_TAKEN = 0.6
const GUARD_DROPPED_DAMAGE_TAKEN = 1.4
const CONCEALED_ATTACK_BONUS = 1.25
/** Attacking a stack you cannot see. Possible, but a poor substitute for a Pathfinder. */
const BLIND_ATTACK_PENALTY = 0.35
const RESISTANCE_DAMAGE_TAKEN = 0.5
const REGENERATE_SHARE = 0.35
/**
 * Rounds a stack spends reloading after a volley. Stored as one more than that,
 * because beginRound decrements every stack's counter — including, at the start
 * of the very next round, the stack that has not yet missed anything.
 */
const VOLLEY_RELOAD_ROUNDS = 1

const SPECIAL_DAMAGE_MULTIPLIER: Partial<Record<BattleActionKind, number>> = {
  volley: 1.8,
  charge: 2.0,
  frenzy: 1.7,
  sunder: 1.2,
  banish: 3.0,
}

/** Banishment only works on things bound to the world; against anything else it is nearly wasted. */
const BANISHABLE_TAGS: UnitTag[] = ["undead", "demon", "anchor"]
const BANISH_MISMATCH_MULTIPLIER = 0.15

// ---------------------------------------------------------------------------
// Seeded randomness
// ---------------------------------------------------------------------------

function createRngSeed(battleId: string, loopNumber: number, day: number): number {
  const text = `${battleId}:${loopNumber}:${day}`
  let seed = 0
  for (let i = 0; i < text.length; i++) {
    seed = (seed * 31 + text.charCodeAt(i)) >>> 0
  }
  return seed || 1
}

/** One mulberry32 step: returns a value in [0, 1) and the next seed. */
function nextRandom(seed: number): [number, number] {
  const t = (seed + 0x6d2b79f5) >>> 0
  let result = Math.imul(t ^ (t >>> 15), t | 1)
  result ^= result + Math.imul(result ^ (result >>> 7), result | 61)
  result = (result ^ (result >>> 14)) >>> 0
  return [result / 4294967296, t]
}

function randomInt(seed: number, min: number, max: number): [number, number] {
  const [r, nextSeed] = nextRandom(seed)
  return [min + Math.floor(r * (max - min + 1)), nextSeed]
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

// ---------------------------------------------------------------------------
// Stack helpers
// ---------------------------------------------------------------------------

function unitOf(stack: BattleStackState): Unit | undefined {
  return getUnitById(stack.unitId)
}

function healthPerUnit(unitId: string): number {
  return getUnitById(unitId)?.health ?? 1
}

/** Derives a stack's remaining unit count from its current health pool. */
export function getRemainingCount(stack: BattleStackState): number {
  return Math.max(0, Math.ceil(stack.currentHealth / healthPerUnit(stack.unitId)))
}

/**
 * Grows a battle-defined stack to the battle's scale, so a stronghold fields a
 * host and a village raid fields a warband — the same fight, read at the size
 * it deserves. A stack of one is a singular figure (a boss, a bound anchor, the
 * Chosen) and is left alone; multiplying it would put twenty Necromancers in
 * the room. Only stacks the battle itself declares pass through here — the
 * player's own army is already sized by their (scaled) Command Points.
 */
function scaledBattleStack(stack: ArmyStack, battle: Battle): ArmyStack {
  const scale = battle.scale ?? 1
  if (scale <= 1 || stack.count <= 1) return stack
  return { ...stack, count: stack.count * scale }
}

function createStackState(
  stack: ArmyStack,
  key: string,
  owner: BattleStackState["owner"],
  side: BattleSide,
): BattleStackState {
  const unit = getUnitById(stack.unitId)
  const pool = stack.count * (unit?.health ?? 1)
  return {
    ...stack,
    stackKey: key,
    currentHealth: pool,
    maxHealth: pool,
    owner,
    side,
    defending: false,
    guardDropped: false,
    concealed: unit?.startsConcealed === true,
    specialUsed: false,
    skipRounds: 0,
  }
}

function replaceStack(
  state: BattleRuntimeState,
  updated: BattleStackState,
): BattleRuntimeState {
  const key: "friendlyStacks" | "enemyStacks" =
    updated.side === "friendly" ? "friendlyStacks" : "enemyStacks"
  return {
    ...state,
    [key]: state[key].map((stack) =>
      stack.stackKey === updated.stackKey ? updated : stack,
    ),
  }
}

function pruneDead(state: BattleRuntimeState): BattleRuntimeState {
  return {
    ...state,
    friendlyStacks: state.friendlyStacks.filter((s) => s.currentHealth > 0),
    enemyStacks: state.enemyStacks.filter((s) => s.currentHealth > 0),
  }
}

function findStack(
  state: BattleRuntimeState,
  stackKey: string,
): BattleStackState | undefined {
  return (
    state.friendlyStacks.find((s) => s.stackKey === stackKey) ??
    state.enemyStacks.find((s) => s.stackKey === stackKey)
  )
}

function opposingStacks(
  state: BattleRuntimeState,
  side: BattleSide,
): BattleStackState[] {
  return side === "friendly" ? state.enemyStacks : state.friendlyStacks
}

// ---------------------------------------------------------------------------
// Player ability modifiers
// ---------------------------------------------------------------------------

export function getPlayerBattleModifiers(
  permanent: PermanentState,
): PlayerBattleModifiers {
  const leadership = getAbilityLevel(permanent, "leadership")
  const archery = getAbilityLevel(permanent, "archery")
  const survival = getAbilityLevel(permanent, "survival")
  const healing = getAbilityLevel(permanent, "healing")
  const swordsmanship = getAbilityLevel(permanent, "swordsmanship")
  const horsemastery = getAbilityLevel(permanent, "horsemastery")
  const arcana = getAbilityLevel(permanent, "arcana")
  const spiritLore = getAbilityLevel(permanent, "spirit_lore")
  const siegecraft = getAbilityLevel(permanent, "siegecraft")
  const tracking = getAbilityLevel(permanent, "tracking")

  const scale = (
    level: AbilityLevel,
    beginner: number,
    advanced: number,
    master: number,
  ): number => {
    if (isAtLeastLevel(level, "master")) return master
    if (isAtLeastLevel(level, "advanced")) return advanced
    if (isAtLeastLevel(level, "beginner")) return beginner
    return 0
  }

  return {
    damageMultiplier: 1 + scale(leadership, 0.05, 0.12, 0.22),
    rangedDamageMultiplier: 1 + scale(archery, 0.1, 0.2, 0.35),
    // Survival turns aside a share of incoming damage; worn armour turns aside
    // another share on top of it. They stack multiplicatively (two independent
    // layers between the blow and the soldier) rather than summing, so neither
    // can ever drive incoming damage to nothing.
    incomingDamageMultiplier:
      (1 - scale(survival, 0.05, 0.1, 0.16)) * (1 - getArmorReduction(permanent)),
    casualtyRecoveryRate: scale(healing, 0.1, 0.2, 0.33),
    // Phase 3 promises Swordsmanship unlocks confronting "elite champions and
    // the anti-player". That has to be worth something in the fight itself, or
    // the master level is a key to a door with nothing behind it — so it scales
    // what the player personally does, which is the "hero" stack they fight as.
    heroDamageMultiplier: 1 + scale(swordsmanship, 0.15, 0.4, 0.85),
    // The formerly inert allied-craft abilities, each a per-family striking
    // bonus that rewards bringing the right troops rather than substituting for
    // them (see the counter system): mounted, arcane, holy, and siege lines.
    cavalryDamageMultiplier: 1 + scale(horsemastery, 0.1, 0.2, 0.35),
    arcaneDamageMultiplier: 1 + scale(arcana, 0.1, 0.2, 0.35),
    holyDamageMultiplier: 1 + scale(spiritLore, 0.1, 0.2, 0.35),
    siegeDamageMultiplier: 1 + scale(siegecraft, 0.2, 0.4, 0.6),
    // Reading a hidden trail is what Tracking is for: from Advanced on, the
    // player's column is not caught by an ambush that has already been read.
    revealsConcealed: isAtLeastLevel(tracking, "advanced"),
  }
}

const NEUTRAL_MODIFIERS: PlayerBattleModifiers = {
  damageMultiplier: 1,
  rangedDamageMultiplier: 1,
  incomingDamageMultiplier: 1,
  casualtyRecoveryRate: 0,
  heroDamageMultiplier: 1,
  cavalryDamageMultiplier: 1,
  arcaneDamageMultiplier: 1,
  holyDamageMultiplier: 1,
  siegeDamageMultiplier: 1,
  revealsConcealed: false,
}

// ---------------------------------------------------------------------------
// Damage
// ---------------------------------------------------------------------------

/** The counter multiplier attacker→target: the strongest matching tag wins. */
export function getCounterMultiplier(attacker: Unit, target: Unit): number {
  if (!attacker.counters) return 1
  return target.tags.reduce(
    (best, tag) => Math.max(best, attacker.counters?.[tag] ?? 1),
    1,
  )
}

export function isResistantTo(target: Unit, damageType: DamageType): boolean {
  return target.resistances.includes(damageType)
}

interface DamageBreakdown {
  damage: number
  counterMultiplier: number
  resisted: boolean
  blind: boolean
  nextSeed: number
}

/**
 * Everything that scales a blow except the per-unit damage roll: the whole-stack
 * multiplier the roll is fed through, plus the flags the UI and log surface.
 * Total damage is `max(1, round(perUnitRoll * multiplier))`, so a min/max roll
 * gives the min/max of the total range — which is what the target preview shows.
 */
interface DamageFactors {
  /** Combined whole-stack multiplier applied to a single per-unit damage roll. */
  multiplier: number
  counterMultiplier: number
  resisted: boolean
  blind: boolean
}

function damageFactors(
  state: BattleRuntimeState,
  attacker: BattleStackState,
  attackerUnit: Unit,
  target: BattleStackState,
  targetUnit: Unit,
  kind: BattleActionKind,
): DamageFactors {
  const attackerCount = Math.max(1, getRemainingCount(attacker))

  const ignoresDefence = kind === "sunder"
  const attackVsDefence = ignoresDefence
    ? 1.5
    : clamp(1 + (attackerUnit.attack - targetUnit.defence) * 0.05, 0.5, 1.5)

  const counterMultiplier = getCounterMultiplier(attackerUnit, targetUnit)

  const resisted = !ignoresDefence && isResistantTo(targetUnit, attackerUnit.damageType)
  const resistanceMultiplier = resisted ? RESISTANCE_DAMAGE_TAKEN : 1

  const specialMultiplier =
    kind === "banish"
      ? targetUnit.tags.some((tag) => BANISHABLE_TAGS.includes(tag))
        ? (SPECIAL_DAMAGE_MULTIPLIER.banish ?? 1)
        : BANISH_MISMATCH_MULTIPLIER
      : (SPECIAL_DAMAGE_MULTIPLIER[kind] ?? 1)

  const targetStanceMultiplier = target.defending
    ? DEFEND_DAMAGE_TAKEN
    : target.guardDropped
      ? GUARD_DROPPED_DAMAGE_TAKEN
      : 1

  const concealmentMultiplier = attacker.concealed ? CONCEALED_ATTACK_BONUS : 1
  const blind = target.concealed
  const blindMultiplier = blind ? BLIND_ATTACK_PENALTY : 1

  // Player abilities help the friendly line only, and never the enemy. Each
  // per-family bonus applies only to the stacks it fits (a mounted, ranged,
  // arcane or holy unit), so they reward a well-matched column without ever
  // touching the wrong troops. Siege is judged on the *target* — the walls and
  // wall-like giants it is meant to break — so it lives in its own factor below.
  const abilityMultiplier =
    attacker.side === "friendly"
      ? state.modifiers.damageMultiplier *
        (attackerUnit.tags.includes("ranged")
          ? state.modifiers.rangedDamageMultiplier
          : 1) *
        (attackerUnit.tags.includes("hero") ? state.modifiers.heroDamageMultiplier : 1) *
        (attackerUnit.tags.includes("cavalry") ? state.modifiers.cavalryDamageMultiplier : 1) *
        (attackerUnit.damageType === "arcane" ? state.modifiers.arcaneDamageMultiplier : 1) *
        (attackerUnit.damageType === "holy" ? state.modifiers.holyDamageMultiplier : 1)
      : 1
  const siegeMultiplier =
    attacker.side === "friendly" &&
    (targetUnit.tags.includes("fortification") || targetUnit.tags.includes("giant"))
      ? state.modifiers.siegeDamageMultiplier
      : 1
  const incomingMultiplier =
    target.side === "friendly" ? state.modifiers.incomingDamageMultiplier : 1

  const multiplier =
    attackerCount *
    attackVsDefence *
    counterMultiplier *
    resistanceMultiplier *
    specialMultiplier *
    targetStanceMultiplier *
    concealmentMultiplier *
    blindMultiplier *
    abilityMultiplier *
    siegeMultiplier *
    incomingMultiplier

  return { multiplier, counterMultiplier, resisted, blind }
}

/** A per-unit roll run through the whole-stack multiplier, floored at 1. */
function totalDamage(perUnitRoll: number, multiplier: number): number {
  return Math.max(1, Math.round(perUnitRoll * multiplier))
}

function computeDamage(
  state: BattleRuntimeState,
  attacker: BattleStackState,
  target: BattleStackState,
  kind: BattleActionKind,
): DamageBreakdown {
  const attackerUnit = unitOf(attacker)
  const targetUnit = unitOf(target)
  if (!attackerUnit || !targetUnit) {
    return {
      damage: 0,
      counterMultiplier: 1,
      resisted: false,
      blind: false,
      nextSeed: state.rngSeed,
    }
  }

  const [perUnitRoll, nextSeed] = randomInt(
    state.rngSeed,
    attackerUnit.damageMin,
    attackerUnit.damageMax,
  )
  const { multiplier, counterMultiplier, resisted, blind } = damageFactors(
    state,
    attacker,
    attackerUnit,
    target,
    targetUnit,
    kind,
  )

  return {
    damage: totalDamage(perUnitRoll, multiplier),
    counterMultiplier,
    resisted,
    blind,
    nextSeed,
  }
}

export interface DamagePreview {
  /** Lowest total damage this blow can deal (min per-unit roll). */
  min: number
  /** Highest total damage this blow can deal (max per-unit roll). */
  max: number
  counterMultiplier: number
  resisted: boolean
  blind: boolean
}

/**
 * The damage range a stack would deal to a target right now — the min and max
 * total across the per-unit roll — for the UI's target preview and for the
 * auto-resolve/enemy AI's target choice. Reads no RNG, so it never changes the
 * battle and is stable across calls.
 */
export function previewDamage(
  state: BattleRuntimeState,
  attackerKey: string,
  targetKey: string,
  kind: BattleActionKind = "attack",
): DamagePreview | null {
  const attacker = findStack(state, attackerKey)
  const target = findStack(state, targetKey)
  if (!attacker || !target) return null
  const attackerUnit = unitOf(attacker)
  const targetUnit = unitOf(target)
  if (!attackerUnit || !targetUnit) return null
  const { multiplier, counterMultiplier, resisted, blind } = damageFactors(
    state,
    attacker,
    attackerUnit,
    target,
    targetUnit,
    kind,
  )
  return {
    min: totalDamage(attackerUnit.damageMin, multiplier),
    max: totalDamage(attackerUnit.damageMax, multiplier),
    counterMultiplier,
    resisted,
    blind,
  }
}

// ---------------------------------------------------------------------------
// Rounds and turn order
// ---------------------------------------------------------------------------

function initiativeOf(stack: BattleStackState): number {
  return unitOf(stack)?.initiative ?? 5
}

/** Every living stack, ordered by initiative. Ties break toward the player's side. */
export function getInitiativeOrder(state: BattleRuntimeState): BattleStackState[] {
  return [...state.friendlyStacks, ...state.enemyStacks].sort((a, b) => {
    const byInitiative = initiativeOf(b) - initiativeOf(a)
    if (byInitiative !== 0) return byInitiative
    if (a.side !== b.side) return a.side === "friendly" ? -1 : 1
    return a.stackKey.localeCompare(b.stackKey)
  })
}

function isReady(state: BattleRuntimeState, stack: BattleStackState): boolean {
  return stack.skipRounds <= 0 && !state.actedKeys.includes(stack.stackKey)
}

/** The stack whose turn it is, or null if the round is over / the battle has ended. */
export function getActiveStack(state: BattleRuntimeState): BattleStackState | null {
  if (state.outcome !== "ongoing") return null
  return getInitiativeOrder(state).find((stack) => isReady(state, stack)) ?? null
}

function applyReinforcements(
  state: BattleRuntimeState,
  battle: Battle,
): BattleRuntimeState {
  const arriving = (battle.reinforcements ?? []).filter(
    (r) => r.round === state.round && !state.reinforcedRounds.includes(r.round),
  )
  if (arriving.length === 0) return state

  let next = { ...state, reinforcedRounds: [...state.reinforcedRounds, state.round] }
  for (const reinforcement of arriving) {
    const newStacks = reinforcement.stacks.map((stack, i) =>
      createStackState(
        scaledBattleStack(stack, battle),
        `reinforce-r${reinforcement.round}-${i}-${stack.unitId}`,
        reinforcement.side === "friendly" ? "ally" : "enemy",
        reinforcement.side,
      ),
    )
    next =
      reinforcement.side === "friendly"
        ? { ...next, friendlyStacks: [...next.friendlyStacks, ...newStacks] }
        : { ...next, enemyStacks: [...next.enemyStacks, ...newStacks] }
    next = { ...next, log: [...next.log, reinforcement.announcement] }
  }
  return next
}

function beginRound(state: BattleRuntimeState, battle: Battle): BattleRuntimeState {
  const refresh = (stack: BattleStackState): BattleStackState => ({
    ...stack,
    defending: false,
    guardDropped: false,
    skipRounds: Math.max(0, stack.skipRounds - 1),
  })
  const advanced: BattleRuntimeState = {
    ...state,
    round: state.round + 1,
    actedKeys: [],
    friendlyStacks: state.friendlyStacks.map(refresh),
    enemyStacks: state.enemyStacks.map(refresh),
  }
  return applyReinforcements(advanced, battle)
}

function determineOutcome(state: BattleRuntimeState): BattleOutcome {
  if (state.outcome === "retreat") return "retreat"
  if (state.enemyStacks.length === 0) return "victory"
  if (state.friendlyStacks.length === 0) return "defeat"
  if (state.round > MAX_ROUNDS) {
    // A grinding stalemate is decided on which line is still standing —
    // better than letting a regenerating troll wall loop forever.
    const share = (stacks: BattleStackState[]) => {
      const max = stacks.reduce((sum, s) => sum + s.maxHealth, 0)
      const now = stacks.reduce((sum, s) => sum + s.currentHealth, 0)
      return max === 0 ? 0 : now / max
    }
    return share(state.friendlyStacks) > share(state.enemyStacks) ? "victory" : "defeat"
  }
  return "ongoing"
}

/**
 * Settles the state after an action: removes destroyed stacks, decides the
 * outcome, and rolls the round forward until some stack is ready to act.
 */
function normalize(state: BattleRuntimeState, battle: Battle): BattleRuntimeState {
  let current = pruneDead(state)
  current = { ...current, outcome: determineOutcome(current) }
  if (current.outcome !== "ongoing") return current

  let guard = 0
  while (!getActiveStack(current) && guard < MAX_ROUNDS + 2) {
    current = beginRound(current, battle)
    current = pruneDead(current)
    const outcome = determineOutcome(current)
    if (outcome !== "ongoing") return { ...current, outcome }
    guard++
  }
  return current
}

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------

/**
 * Initializes a battle. The friendly side is the player's current army plus
 * any allies defined on the battle itself (garrison defenders, mustered
 * contingents) who fight alongside the player without joining currentArmy.
 * loopNumber/day seed the combat RNG so a given attempt is reproducible but
 * varies loop to loop.
 */
export function initializeBattle(
  battle: Battle,
  playerStacks: ArmyStack[],
  loopNumber: number,
  day: number,
  modifiers: PlayerBattleModifiers = NEUTRAL_MODIFIERS,
): BattleRuntimeState {
  const friendly = [
    ...playerStacks.map((stack, i) =>
      createStackState(stack, `player-${i}-${stack.unitId}`, "player", "friendly"),
    ),
    ...(battle.alliedStacks ?? []).map((stack, i) =>
      createStackState(
        scaledBattleStack(stack, battle),
        `ally-${i}-${stack.unitId}`,
        "ally",
        "friendly",
      ),
    ),
  ]
  // Tracking (Advanced+) reads the ground before the first blow, so an enemy
  // that would have opened from concealment (Dark Elves, Vane's ambushers) is
  // already in the open when the line forms. Nothing else reveals a stack for
  // free — without this it takes a Pathfinder's reveal mid-battle.
  const enemyStacks = battle.enemyStacks.map((stack, i) => {
    const state = createStackState(
      scaledBattleStack(stack, battle),
      `enemy-${i}-${stack.unitId}`,
      "enemy",
      "enemy",
    )
    return modifiers.revealsConcealed ? { ...state, concealed: false } : state
  })
  const initial: BattleRuntimeState = {
    battleId: battle.id,
    round: 0,
    friendlyStacks: friendly,
    enemyStacks,
    rngSeed: createRngSeed(battle.id, loopNumber, day),
    actedKeys: [],
    log: [],
    outcome: "ongoing",
    modifiers,
    reinforcedRounds: [],
    completedObjectiveIds: [],
    initialFriendlyKeys: friendly.map((stack) => stack.stackKey),
  }
  // beginRound takes the battle into round 1, so round numbers the player sees
  // match the objectives ("win within six rounds") and any reinforcement
  // scheduled for round 1 arrives before the first blow rather than after it.
  return normalize(beginRound(initial, battle), battle)
}

/** The player's own surviving stacks (excluding battle-only allies) — flows back into LoopState.currentArmy. */
export function getPlayerSurvivingStacks(state: BattleRuntimeState): ArmyStack[] {
  return state.friendlyStacks
    .filter((stack) => stack.owner === "player")
    .map((stack) => ({ unitId: stack.unitId, count: getRemainingCount(stack) }))
    .filter((stack) => stack.count > 0)
}

export function getBattleOutcome(state: BattleRuntimeState): BattleOutcome {
  return state.outcome
}

// ---------------------------------------------------------------------------
// Targeting
// ---------------------------------------------------------------------------

/**
 * Who a stack may attack. Concealed enemies (Dark Elves) are untargetable
 * until something reveals them — but if the whole enemy line is hidden the
 * player may still swing blindly, at a heavy penalty, rather than being stuck.
 */
export function getTargetableStacks(
  state: BattleRuntimeState,
  attacker: BattleStackState,
): BattleStackState[] {
  const enemies = opposingStacks(state, attacker.side)
  const visible = enemies.filter((stack) => !stack.concealed)
  return visible.length > 0 ? visible : enemies
}

/** Whether this stack's unit type has a special action still available to it. */
export function getAvailableSpecial(stack: BattleStackState): BattleActionKind | null {
  if (stack.specialUsed) return null
  const special = unitOf(stack)?.specialAction
  return special ? special.kind : null
}

export function getSpecialAction(stack: BattleStackState) {
  return unitOf(stack)?.specialAction ?? null
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

function markActed(
  state: BattleRuntimeState,
  stack: BattleStackState,
): BattleRuntimeState {
  return { ...state, actedKeys: [...state.actedKeys, stack.stackKey] }
}

function describeAttack(
  attackerUnit: Unit,
  targetUnit: Unit,
  kind: BattleActionKind,
  breakdown: DamageBreakdown,
  destroyed: boolean,
): string {
  const verb =
    kind === "attack"
      ? "attacks"
      : `uses ${attackerUnit.specialAction?.name ?? kind} on`
  const notes: string[] = []
  if (breakdown.counterMultiplier > 1) {
    notes.push(`counters ${targetUnit.name} (x${breakdown.counterMultiplier})`)
  }
  if (breakdown.resisted) notes.push(`${targetUnit.name} resists ${attackerUnit.damageType}`)
  if (breakdown.blind) notes.push("striking blind at a hidden enemy")
  const suffix = notes.length > 0 ? ` [${notes.join("; ")}]` : ""
  return `${attackerUnit.name} ${verb} ${targetUnit.name} for ${breakdown.damage} damage.${suffix}${
    destroyed ? ` ${targetUnit.name} is destroyed.` : ""
  }`
}

function performAttack(
  state: BattleRuntimeState,
  battle: Battle,
  attacker: BattleStackState,
  target: BattleStackState,
  kind: BattleActionKind,
): BattleRuntimeState {
  const attackerUnit = unitOf(attacker)
  const targetUnit = unitOf(target)
  if (!attackerUnit || !targetUnit) return normalize(markActed(state, attacker), battle)

  const breakdown = computeDamage(state, attacker, target, kind)
  const newHealth = Math.max(0, target.currentHealth - breakdown.damage)

  let next: BattleRuntimeState = { ...state, rngSeed: breakdown.nextSeed }
  next = replaceStack(next, { ...target, currentHealth: newHealth })

  // A stack that commits to a charge or a frenzy leaves itself open; a volley
  // leaves the crews reloading through the next round. Concealment is *not*
  // given away by attacking — Dark Elves strike from cover and stay there, so
  // the only reliable answer is a unit that can actually find them.
  const attackerAfter: BattleStackState = {
    ...attacker,
    specialUsed: attacker.specialUsed || kind !== "attack",
    guardDropped: kind === "charge" || kind === "frenzy",
    skipRounds: kind === "volley" ? VOLLEY_RELOAD_ROUNDS + 1 : attacker.skipRounds,
  }
  next = replaceStack(next, attackerAfter)
  next = {
    ...next,
    log: [
      ...next.log,
      describeAttack(attackerUnit, targetUnit, kind, breakdown, newHealth <= 0),
    ],
  }
  return normalize(markActed(next, attacker), battle)
}

function performDefend(
  state: BattleRuntimeState,
  battle: Battle,
  stack: BattleStackState,
): BattleRuntimeState {
  const name = unitOf(stack)?.name ?? stack.unitId
  let next = replaceStack(state, { ...stack, defending: true, guardDropped: false })
  next = { ...next, log: [...next.log, `${name} braces and defends.`] }
  return normalize(markActed(next, stack), battle)
}

function performShieldwall(
  state: BattleRuntimeState,
  battle: Battle,
  stack: BattleStackState,
): BattleRuntimeState {
  const name = unitOf(stack)?.name ?? stack.unitId
  const key: "friendlyStacks" | "enemyStacks" =
    stack.side === "friendly" ? "friendlyStacks" : "enemyStacks"
  const next: BattleRuntimeState = {
    ...state,
    [key]: state[key].map((s) => ({
      ...s,
      defending: true,
      guardDropped: false,
      specialUsed: s.stackKey === stack.stackKey ? true : s.specialUsed,
    })),
    log: [...state.log, `${name} locks shields — the whole line braces behind them.`],
  }
  return normalize(markActed(next, stack), battle)
}

function performRegenerate(
  state: BattleRuntimeState,
  battle: Battle,
  stack: BattleStackState,
): BattleRuntimeState {
  const name = unitOf(stack)?.name ?? stack.unitId
  const healed = Math.min(
    stack.maxHealth,
    stack.currentHealth + Math.round(stack.maxHealth * REGENERATE_SHARE),
  )
  const recovered = healed - stack.currentHealth
  let next = replaceStack(state, {
    ...stack,
    currentHealth: healed,
    specialUsed: true,
  })
  next = {
    ...next,
    log: [...next.log, `${name} knits its wounds closed, recovering ${recovered} health.`],
  }
  return normalize(markActed(next, stack), battle)
}

function performReveal(
  state: BattleRuntimeState,
  battle: Battle,
  stack: BattleStackState,
): BattleRuntimeState {
  const name = unitOf(stack)?.name ?? stack.unitId
  const enemyKey: "friendlyStacks" | "enemyStacks" =
    stack.side === "friendly" ? "enemyStacks" : "friendlyStacks"
  const concealedCount = state[enemyKey].filter((s) => s.concealed).length
  let next: BattleRuntimeState = {
    ...state,
    [enemyKey]: state[enemyKey].map((s) => ({ ...s, concealed: false })),
  }
  next = replaceStack(next, { ...stack, specialUsed: true })
  next = {
    ...next,
    log: [
      ...next.log,
      concealedCount > 0
        ? `${name} reads the ground and calls out ${concealedCount} hidden position${
            concealedCount === 1 ? "" : "s"
          }. The ambush is broken.`
        : `${name} sweeps the ground, but nothing is hiding out there.`,
    ],
  }
  return normalize(markActed(next, stack), battle)
}

/**
 * Applies one action for the stack whose turn it is. Attacks and offensive
 * specials need a targetKey; defensive and self-affecting ones do not.
 */
export function performAction(
  state: BattleRuntimeState,
  battle: Battle,
  stackKey: string,
  kind: BattleActionKind,
  targetKey?: string,
): BattleRuntimeState {
  if (state.outcome !== "ongoing") return state
  const active = getActiveStack(state)
  if (!active || active.stackKey !== stackKey) return state

  if (kind !== "attack" && kind !== "defend" && active.specialUsed) return state

  switch (kind) {
    case "defend":
      return performDefend(state, battle, active)
    case "shieldwall":
      return performShieldwall(state, battle, active)
    case "regenerate":
      return performRegenerate(state, battle, active)
    case "reveal":
      return performReveal(state, battle, active)
    default: {
      if (!targetKey) return state
      const target = opposingStacks(state, active.side).find(
        (s) => s.stackKey === targetKey,
      )
      if (!target) return state
      const targetable = getTargetableStacks(state, active)
      if (!targetable.some((s) => s.stackKey === targetKey)) return state
      return performAttack(state, battle, active, target, kind)
    }
  }
}

/**
 * Withdrawing from a battle that narratively permits it. The army escapes but
 * pays for it — a quarter of every stack is lost covering the retreat.
 */
export function retreat(state: BattleRuntimeState): BattleRuntimeState {
  return {
    ...state,
    outcome: "retreat",
    friendlyStacks: state.friendlyStacks
      .map((stack) => ({
        ...stack,
        currentHealth: Math.floor(stack.currentHealth * 0.75),
      }))
      .filter((stack) => stack.currentHealth > 0),
    log: [
      ...state.log,
      "You break off and withdraw. The rearguard does not all make it out.",
    ],
  }
}

// ---------------------------------------------------------------------------
// Automatic play (enemy turns, and the player's own auto-resolve)
// ---------------------------------------------------------------------------

interface ScoredChoice {
  kind: BattleActionKind
  targetKey?: string
  score: number
}

/**
 * Picks a reasonable action for a stack. Used both for the enemy AI and for
 * the player's auto-resolve, so a fight the player doesn't want to click
 * through resolves the way a competent commander would have played it.
 */
function chooseAction(
  state: BattleRuntimeState,
  stack: BattleStackState,
): ScoredChoice {
  const special = getAvailableSpecial(stack)

  // Self-affecting specials are judged on their own terms first.
  if (special === "regenerate" && stack.currentHealth < stack.maxHealth * 0.6) {
    return { kind: "regenerate", score: Infinity }
  }
  if (special === "reveal" && opposingStacks(state, stack.side).some((s) => s.concealed)) {
    return { kind: "reveal", score: Infinity }
  }

  const targets = getTargetableStacks(state, stack)
  if (targets.length === 0) return { kind: "defend", score: 0 }

  const offensiveKinds: BattleActionKind[] = ["attack"]
  if (
    special &&
    special !== "regenerate" &&
    special !== "reveal" &&
    special !== "shieldwall"
  ) {
    offensiveKinds.push(special)
  }

  let best: ScoredChoice = { kind: "attack", targetKey: targets[0].stackKey, score: -1 }
  for (const kind of offensiveKinds) {
    for (const target of targets) {
      const preview = previewDamage(state, stack.stackKey, target.stackKey, kind)
      if (!preview) continue
      // Judge a blow by its expected damage — the midpoint of its range.
      const expected = (preview.min + preview.max) / 2
      // Overkill is wasted, so damage is valued only up to what the target
      // still has — which is what makes counter units get used on the things
      // they counter rather than on whatever is merely biggest.
      const effective = Math.min(expected, target.currentHealth)
      const finishingBonus = expected >= target.currentHealth ? effective * 0.5 : 0
      // A volley costs the stack its next round, so it needs to earn that.
      const cost = kind === "volley" ? 0.7 : 1
      const score = (effective + finishingBonus) * cost
      if (score > best.score) best = { kind, targetKey: target.stackKey, score }
    }
  }

  // A shieldwall is worth more than a weak swing when the line is under real pressure.
  if (special === "shieldwall") {
    const allies = stack.side === "friendly" ? state.friendlyStacks : state.enemyStacks
    const pressure = allies.reduce((sum, s) => sum + s.currentHealth / s.maxHealth, 0) / allies.length
    if (pressure < 0.5 && best.score < stack.maxHealth * 0.2) {
      return { kind: "shieldwall", score: best.score + 1 }
    }
  }

  return best
}

/** Runs one automatic turn for whichever stack is currently active. */
function stepAutomatically(
  state: BattleRuntimeState,
  battle: Battle,
): BattleRuntimeState {
  const active = getActiveStack(state)
  if (!active) return state
  const choice = chooseAction(state, active)
  return performAction(state, battle, active.stackKey, choice.kind, choice.targetKey)
}

/**
 * Plays enemy turns until it is a friendly stack's turn again (or the battle
 * ends), so the player is only ever asked to act for their own stacks.
 */
export function advanceToPlayerTurn(
  state: BattleRuntimeState,
  battle: Battle,
): BattleRuntimeState {
  let current = state
  let guard = 0
  while (guard < 500) {
    const active = getActiveStack(current)
    if (!active || active.side === "friendly") return current
    const next = stepAutomatically(current, battle)
    if (next === current) return current
    current = next
    guard++
  }
  return current
}

/**
 * Resolves the entire remaining battle automatically, both sides. Phase 5 asks
 * that many battles not become exhausting; this is the escape hatch for fights
 * whose outcome the player's army composition has already decided.
 */
export function autoResolve(
  state: BattleRuntimeState,
  battle: Battle,
): BattleRuntimeState {
  let current = state
  let guard = 0
  while (current.outcome === "ongoing" && guard < 2000) {
    const next = stepAutomatically(current, battle)
    if (next === current) break
    current = next
    guard++
  }
  return current
}

// ---------------------------------------------------------------------------
// Objectives
// ---------------------------------------------------------------------------

export interface ObjectiveResult {
  objective: BattleObjective
  achieved: boolean
}

/**
 * Evaluates a battle's objectives against the final state. Objectives are
 * scored only on a win — losing the field forfeits them all.
 *
 * The friendly-side objectives are judged against the stacks the battle
 * *started* with. Reinforcements are not a substitute for the line that was
 * lost, and the player's own copy of a unit type is not the ally they were
 * asked to protect.
 */
export function evaluateObjectives(
  battle: Battle,
  state: BattleRuntimeState,
): ObjectiveResult[] {
  const objectives = battle.objectives ?? []
  const won = state.outcome === "victory"
  const startingSurvivors = state.friendlyStacks.filter((s) =>
    state.initialFriendlyKeys.includes(s.stackKey),
  )
  return objectives.map((objective) => {
    if (!won) return { objective, achieved: false }
    switch (objective.kind) {
      case "destroy_unit":
        return {
          objective,
          achieved: !state.enemyStacks.some((s) => s.unitId === objective.unitId),
        }
      case "win_within_rounds":
        return { objective, achieved: state.round <= (objective.rounds ?? Infinity) }
      case "no_stack_lost":
        return {
          objective,
          achieved: startingSurvivors.length >= state.initialFriendlyKeys.length,
        }
      case "protect_ally":
        return {
          objective,
          achieved: startingSurvivors.some(
            (s) => s.owner === "ally" && s.unitId === objective.unitId,
          ),
        }
      default:
        return { objective, achieved: false }
    }
  })
}

export interface BattleResolution {
  outcome: Exclude<BattleOutcome, "ongoing">
  /** World flags to apply: the battle's own, plus any earned objective rewards. */
  flags: string[]
  lootMoney: number
  /**
   * Command Points a won battle earns for the rest of the loop, in the battle's
   * authored (unscaled) units — the caller multiplies by ARMY_SCALE when it
   * grants them. Zero on any non-victory outcome.
   */
  commandPointReward: number
  objectives: ObjectiveResult[]
  survivingPlayerArmy: ArmyStack[]
}

/**
 * Turns a finished battle into everything the world needs to know about it.
 * Objectives are scored here, so a won fight that missed its objective is
 * visibly a lesser result than one that met it.
 */
export function resolveBattle(
  battle: Battle,
  state: BattleRuntimeState,
): BattleResolution {
  const outcome = state.outcome === "ongoing" ? "defeat" : state.outcome
  const objectives = evaluateObjectives(battle, state)
  const achieved = objectives.filter((result) => result.achieved)

  const baseFlags =
    outcome === "victory"
      ? battle.victoryFlags
      : outcome === "retreat"
        ? (battle.retreatFlags ?? battle.defeatFlags)
        : battle.defeatFlags

  return {
    outcome,
    flags: [
      ...baseFlags,
      ...achieved.flatMap((result) => result.objective.rewardFlags ?? []),
    ],
    lootMoney:
      outcome === "victory"
        ? (battle.lootMoney ?? 0) +
          achieved.reduce((sum, r) => sum + (r.objective.rewardMoney ?? 0), 0)
        : 0,
    commandPointReward: outcome === "victory" ? (battle.commandPointReward ?? 0) : 0,
    objectives,
    survivingPlayerArmy: getPlayerSurvivingStacks(state),
  }
}

/**
 * Casualties the player's Healing ability recovers after a won battle: the
 * walking wounded who would otherwise have been written off.
 */
export function applyHealingRecovery(
  before: ArmyStack[],
  after: ArmyStack[],
  recoveryRate: number,
): ArmyStack[] {
  if (recoveryRate <= 0) return after
  return before
    .map((original) => {
      const survivors = after.find((s) => s.unitId === original.unitId)?.count ?? 0
      const lost = original.count - survivors
      if (lost <= 0) return { unitId: original.unitId, count: survivors }
      return {
        unitId: original.unitId,
        count: survivors + Math.floor(lost * recoveryRate),
      }
    })
    .filter((stack) => stack.count > 0)
}
